import { enrichAIFlowWithImpactScore } from '@src/aiFlow/impactScore';
import {
  buildClipFragment,
  getFragmentPosition,
} from '@src/services/AIClipProducer';
import { LogManager } from '@src/utility/Logger';

import {
  buildPlainTranscriptWithSpeakers,
  buildTranscriptWithIndices,
} from './buildTranscriptWithIndices';

import type { BeatClipOption, BeatClipResult, ReelClipResults } from './types';
import type { AIFlowStep } from '@src/aiFlow/AIFlowStep';
import type { AudienceOption } from '@src/components/common/AudienceSelector';
import type { GoalOption } from '@src/components/common/GoalSelector';
import type { ReelOutline } from '@src/components/content-components/ReelProducer/types';
import type { Speaker } from '@src/components/content-components/SpeakerTypes';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { RootStore } from '@src/stores-v2/RootStore';
import type { StoryVideo } from '@src/types.ts/story';
import type { TranscriptElement } from '@src/types.ts/transcript';

const logger = LogManager.getLogger('reelClipProducer:produceReelClips');

const NUMBER_OF_WORDS_TO_MATCH = 5;

function stripPunctuationFromEnds(text: string) {
  return text.replaceAll(/^[^\w\s]+|[^\w\s]+$/g, '');
}

export type ProduceReelClipsParams = {
  outline: ReelOutline;
  transcriptElements: TranscriptElement[];
  originalVideo: StoryVideo;
  videoDuration: number;
  rootStore: RootStore;
  videoCreator: IVideoCreatorStore;
  allSpeakers: Speaker[];
  selectedSpeakers: Speaker[];
  selectedGoal: GoalOption | null;
  selectedAudiences: AudienceOption[];
  videoTemplateId: string | null;
  onPartialResults?: (results: ReelClipResults) => void;
};

/**
 * Build the speaker label-to-name map from the list of speakers.
 */
function buildSpeakerLabelMap(speakers: Speaker[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const s of speakers) {
    if (s.speakerLabel && s.name) {
      map.set(s.speakerLabel, s.name);
    }
    if (s.allSpeakerLabels) {
      for (const label of s.allSpeakerLabels) {
        if (s.name) map.set(label, s.name);
      }
    }
  }
  return map;
}

/**
 * Build speaker context string from selected speakers.
 */
function buildSpeakerContext(speakers: Speaker[]): string {
  if (speakers.length === 0) return '';
  return speakers
    .map(
      s =>
        `${s.name}${s.title ? ` (${s.title})` : ''}${s.speakerLabel ? ` [label: ${s.speakerLabel}]` : ''}`,
    )
    .join(', ');
}

/**
 * Build a flat ordered list of beat keys from the outline.
 * The AI returns clips in the same order as beats, so id 0 = first beat, id 1 = second beat, etc.
 */
function buildBeatKeyList(outline: ReelOutline): string[] {
  const keys: string[] = [];
  for (const chapter of outline.chapters) {
    for (const beat of chapter.beats) {
      keys.push(`${chapter.order}.${beat.order}`);
    }
  }
  return keys;
}

/**
 * Process standard clip producer format: array of { id, title, text_fragment, impact_score }.
 * Maps each clip to a beat by id order, resolves transcript positions via text matching.
 */
function processTextFragmentClips(
  clips: any[],
  beatKeys: string[],
  beatMeta: Map<string, { chapterTitle: string; beatDescription: string; estimatedDuration: number }>,
  transcriptElements: TranscriptElement[],
  originalVideo: StoryVideo,
  speakers: Speaker[],
  stepContext: AIFlowStep,
  videoTemplateId: string | null,
  idOffset: number = 0,
): ReelClipResults {
  const results: ReelClipResults = new Map();

  for (let i = 0; i < clips.length; i++) {
    const clip = clips[i];
    const clipId = clip.id ?? (i + idOffset);
    const beatKey = beatKeys[clipId];
    if (!beatKey) {
      logger.warn(`Clip ${clipId} has no matching beat (only ${beatKeys.length} beats)`);
      continue;
    }

    const meta = beatMeta.get(beatKey) ?? { chapterTitle: '', beatDescription: '', estimatedDuration: 15 };
    // Allow 50%-150% of estimated duration to keep clips close to target
    const beatDurationMin = Math.max(5, Math.floor(meta.estimatedDuration * 0.5));
    const beatDurationMax = Math.min(90, Math.ceil(meta.estimatedDuration * 1.5));

    const textFragment: string = clip.text_fragment ?? '';
    const title: string = clip.title ?? '';
    const impactScore: Record<string, number> = clip.impact_score ?? {};
    const justification: string = clip.justification ?? '';

    logger.debug(`Beat ${beatKey} impact_score keys: ${JSON.stringify(Object.keys(impactScore))}, values: ${JSON.stringify(impactScore)}`);

    // Text-match: find first 5 and last 5 words in transcript
    const beginning = stripPunctuationFromEnds(
      textFragment.split(' ').slice(0, NUMBER_OF_WORDS_TO_MATCH).join(' '),
    );
    const ending = stripPunctuationFromEnds(
      textFragment.split(' ').slice(-NUMBER_OF_WORDS_TO_MATCH).join(' '),
    );

    logger.debug(`Beat ${beatKey} (clip ${clipId}): matching "${beginning}" ... "${ending}"`);

    // Find ALL matches for the beginning text, pair with endings, pick best by duration proximity
    const targetDuration = meta.estimatedDuration;
    type Match = { startIndex: number; endIndex: number; startTime: number; endTime: number; duration: number };
    const candidates: Match[] = [];

    let searchFrom = 0;
    for (let attempt = 0; attempt < 10; attempt++) {
      const bPos = getFragmentPosition(beginning, transcriptElements, searchFrom);
      if (!bPos?.startTime || !bPos?.transcriptPosition) break;

      const ePos = getFragmentPosition(ending, transcriptElements, bPos.transcriptPosition.startIndex);
      if (ePos?.endTime && ePos?.transcriptPosition) {
        const dur = ePos.endTime - bPos.startTime;
        if (dur > 0) {
          candidates.push({
            startIndex: bPos.transcriptPosition.startIndex,
            endIndex: ePos.transcriptPosition.endIndex,
            startTime: bPos.startTime,
            endTime: ePos.endTime,
            duration: dur,
          });
        }
      }
      // Continue searching after this match
      searchFrom = bPos.transcriptPosition.endIndex + 1;
    }

    if (candidates.length === 0) {
      logger.debug(`Beat ${beatKey}: no text match found`);
      results.set(beatKey, {
        chapterTitle: meta.chapterTitle,
        beatDescription: meta.beatDescription,
        options: [{
          title, startIndex: -1, endIndex: -1, text: textFragment,
          scores: { relevance: 0, narrativeFlow: 0, speakerMatch: 0, completeness: 0 },
          overallScore: impactScore['Final impact score'] ?? 0,
          recommended: true,
          justification,
          rejectReason: `Text not found in transcript: "${beginning}" ... "${ending}"`,
        }],
        selectedOptionIndex: 0,
      });
      continue;
    }

    // Pick the candidate whose duration is closest to the target
    const best = candidates.reduce((a, b) =>
      Math.abs(a.duration - targetDuration) <= Math.abs(b.duration - targetDuration) ? a : b,
    );

    if (candidates.length > 1) {
      logger.debug(`Beat ${beatKey}: ${candidates.length} matches found (durations: ${candidates.map(c => c.duration.toFixed(1) + 's').join(', ')}), picked ${best.duration.toFixed(1)}s (target ${targetDuration}s)`);
    }

    let { startIndex, endIndex, startTime, endTime } = best;
    let fragmentDuration = endTime - startTime;

    // If the fragment is too long, trim from the end to fit within bounds
    if (fragmentDuration > beatDurationMax) {
      logger.debug(`Beat ${beatKey}: trimming from ${fragmentDuration.toFixed(1)}s to fit max ${beatDurationMax}s`);
      const targetEndTime = startTime + beatDurationMax;
      // Walk backward through transcript elements to find a good end point
      for (let ei = endIndex; ei > startIndex; ei--) {
        const el = transcriptElements[ei];
        if (el && el.end_ts != null && el.end_ts <= targetEndTime) {
          endIndex = ei;
          endTime = el.end_ts + ((el as any).buffer_after_ts || 0);
          fragmentDuration = endTime - startTime;
          break;
        }
      }
      logger.debug(`Beat ${beatKey}: trimmed to ${startTime.toFixed(1)}s-${endTime.toFixed(1)}s (${fragmentDuration.toFixed(1)}s)`);
    }

    logger.debug(`Beat ${beatKey}: ${startTime.toFixed(1)}s-${endTime.toFixed(1)}s (${fragmentDuration.toFixed(1)}s), bounds ${beatDurationMin}-${beatDurationMax}s`);

    const clipFragment = buildClipFragment({
      partIndex: i,
      startIndex, endIndex, startTime, endTime,
      title, textFragment,
      transcriptionElements: transcriptElements,
      originalVideo,
      platform: undefined,
      theme: 'reel', themeDisplayName: 'Reel',
      durationMin: beatDurationMin, durationMax: beatDurationMax,
      speakers, stepContext,
      prevResultFragments: [],
      impactScore,
      videoTemplateId: videoTemplateId ?? undefined,
    });

    // Compute reject reason if buildClipFragment returned null
    let rejectReason: string | undefined;
    if (!clipFragment) {
      if (fragmentDuration < beatDurationMin) {
        rejectReason = `Too short: ${fragmentDuration.toFixed(1)}s (min ${beatDurationMin}s for ${meta.estimatedDuration}s beat)`;
      } else if (fragmentDuration > beatDurationMax) {
        rejectReason = `Too long after trim: ${fragmentDuration.toFixed(1)}s (max ${beatDurationMax}s for ${meta.estimatedDuration}s beat)`;
      } else {
        rejectReason = `Rejected by clip builder (${fragmentDuration.toFixed(1)}s)`;
      }
    }

    logger.debug(`Beat ${beatKey}: ${clipFragment ? `RESOLVED (${clipFragment.startTime.toFixed(1)}s, ${clipFragment.duration.toFixed(1)}s)` : rejectReason}`);

    const overallScore = impactScore['Final impact score'] ?? 0;
    results.set(beatKey, {
      chapterTitle: meta.chapterTitle,
      beatDescription: meta.beatDescription,
      options: [{
        title, startIndex, endIndex, text: textFragment,
        scores: {
          relevance: impactScore['Relevance'] ?? 0,
          narrativeFlow: impactScore['Engagement'] ?? 0,
          speakerMatch: impactScore['Audience'] ?? 0,
          completeness: impactScore['Institutional Insight'] ?? 0,
        },
        overallScore, recommended: true,
        justification,
        clipFragment: clipFragment ?? undefined,
        rejectReason,
      }],
      selectedOptionIndex: 0,
    });
  }

  const resolvedCount = Array.from(results.values()).reduce(
    (n, r) => n + (r.options[0]?.clipFragment ? 1 : 0), 0,
  );
  logger.info(`Text-fragment mapping complete: ${results.size} beats, ${resolvedCount} resolved`);
  return results;
}

/**
 * Creates the mapper for the text-based reel response format.
 *
 * The AI returns streaming text clips (same format as regular clip producer):
 *   { id, title, text_fragment, impact_score, justification }
 *
 * The mapper processes clips incrementally as they stream in:
 * 1. On each partial result, processes only newly arrived clips
 * 2. For each clip, uses getFragmentPosition to find the text in the transcript
 * 3. Builds ClipFragments with real source video timestamps
 * 4. Maps clips back to beats by order (id 0 = first beat, etc.)
 * 5. Accumulates results and emits partial updates via onPartialResults
 */
function createReelClipResultMapper({
  outline,
  transcriptElements,
  originalVideo,
  speakers,
  videoTemplateId,
}: {
  outline: ReelOutline;
  transcriptElements: TranscriptElement[];
  originalVideo: StoryVideo;
  speakers: Speaker[];
  videoTemplateId: string | null;
}): (flowContext: any, stepContext: AIFlowStep) => ReelClipResults | null {
  const beatKeys = buildBeatKeyList(outline);

  // Build a map of beat key -> metadata including estimated duration for bounds
  const beatMeta = new Map<string, {
    chapterTitle: string;
    beatDescription: string;
    estimatedDuration: number;
  }>();
  for (const chapter of outline.chapters) {
    for (const beat of chapter.beats) {
      beatMeta.set(`${chapter.order}.${beat.order}`, {
        chapterTitle: chapter.title,
        beatDescription: beat.description,
        estimatedDuration: beat.estimatedDuration,
      });
    }
  }

  // Track how many clips we've already processed so we only process new ones
  let processedCount = 0;
  let partialResults: ReelClipResults = new Map();

  return (_flowContext: any, stepContext: AIFlowStep): ReelClipResults | null => {
    const lastResponse = stepContext.lastResponse;

    if (!lastResponse) {
      if (stepContext.doneStreaming) {
        logger.warn('No response received from reel clip producer flow');
      }
      return null;
    }

    // Try to extract the batch beats object from the response.
    // The AI may return it in several ways:
    // 1. An array with a single object whose text_fragment contains JSON with "beats"
    // 2. An array with a single object that directly has a "beats" property
    // 3. A direct object with "beats"
    let beatsObj: Record<string, any> | null = null;

    if (Array.isArray(lastResponse)) {
      for (const item of lastResponse) {
        // Check if text_fragment contains the batch JSON
        if (item?.text_fragment && typeof item.text_fragment === 'string') {
          try {
            const parsed = JSON.parse(item.text_fragment);
            if (parsed?.beats) {
              beatsObj = parsed.beats;
              break;
            }
          } catch {
            // not JSON, skip
          }
        }
        // Check if the item itself has beats
        if (item?.beats && typeof item.beats === 'object') {
          beatsObj = item.beats;
          break;
        }
      }
    } else if (typeof lastResponse === 'object' && (lastResponse as any)?.beats) {
      beatsObj = (lastResponse as any).beats;
    }

    // If no batch format found, try standard clip producer format:
    // Array of { id, title, text_fragment, impact_score } — one per beat
    if (!beatsObj && Array.isArray(lastResponse) && lastResponse.length > 0 && lastResponse[0]?.text_fragment) {
      // Process only NEW clips that arrived since last call
      const newClips = lastResponse.slice(processedCount);
      if (newClips.length > 0) {
        logger.debug(`Processing ${newClips.length} new clips (${processedCount} already processed, ${lastResponse.length} total)`);
        const newResults = processTextFragmentClips(newClips, beatKeys, beatMeta, transcriptElements, originalVideo, speakers, stepContext, videoTemplateId, processedCount);
        // Merge new results into partial results
        for (const [key, value] of newResults) {
          partialResults.set(key, value);
        }
        processedCount = lastResponse.length;
      }
      // During streaming, return a copy of partial results so far
      // On done, return the final accumulated results
      return partialResults.size > 0 ? new Map(partialResults) : null;
    }

    if (!beatsObj) {
      logger.warn('Could not find beats in response');
      return null;
    }

    logger.debug('Found beats:', Object.keys(beatsObj));

    const results: ReelClipResults = new Map();

    for (const [beatKey, beatData] of Object.entries(beatsObj)) {
      if (!beatData?.options || !Array.isArray(beatData.options)) {
        logger.warn(`Beat ${beatKey}: no options array`);
        continue;
      }

      const meta = beatMeta.get(beatKey) ?? { chapterTitle: beatData.chapter_title ?? '', beatDescription: beatData.beat_description ?? '', estimatedDuration: 15 };
      const beatDurationMin = Math.max(5, Math.floor(meta.estimatedDuration * 0.5));
      const beatDurationMax = Math.ceil(meta.estimatedDuration * 1.5);

      const options: BeatClipOption[] = [];
      let selectedOptionIndex = 0;

      for (let optIdx = 0; optIdx < beatData.options.length; optIdx++) {
        const opt = beatData.options[optIdx];
        const startIdx = typeof opt.start_index === 'number' ? opt.start_index : -1;
        const endIdx = typeof opt.end_index === 'number' ? opt.end_index : -1;
        const text = opt.text ?? '';
        const title = opt.title ?? '';
        const scores = opt.scores ?? {};
        const overallScore = opt.overall_score ?? 0;
        const recommended = opt.recommended ?? false;
        const justification: string = opt.justification ?? '';

        // Try index-based resolution first
        let clipFragment: ReturnType<typeof buildClipFragment> = null;

        if (startIdx >= 0 && endIdx >= 0 && endIdx < transcriptElements.length && startIdx <= endIdx) {
          // Find timestamps from transcript elements
          let startEl: TranscriptElement | undefined;
          for (let si = startIdx; si <= endIdx; si++) {
            const el = transcriptElements[si];
            if (el && el.ts != null) { startEl = el; break; }
          }
          let endEl: TranscriptElement | undefined;
          for (let ei = endIdx; ei >= startIdx; ei--) {
            const el = transcriptElements[ei];
            if (el && el.end_ts != null) { endEl = el; break; }
          }

          if (startEl?.ts != null && endEl?.end_ts != null) {
            const startTime = startEl.ts - ((startEl as any).buffer_before_ts || 0);
            let trimmedEndIdx = endIdx;
            let endTime = endEl.end_ts + ((endEl as any).buffer_after_ts || 0);
            let fragDur = endTime - startTime;

            // Trim if too long
            if (fragDur > beatDurationMax) {
              const targetEnd = startTime + beatDurationMax;
              for (let ei = endIdx; ei > startIdx; ei--) {
                const el = transcriptElements[ei];
                if (el && el.end_ts != null && el.end_ts <= targetEnd) {
                  trimmedEndIdx = ei;
                  endTime = el.end_ts + ((el as any).buffer_after_ts || 0);
                  fragDur = endTime - startTime;
                  break;
                }
              }
              logger.debug(`Beat ${beatKey} opt ${optIdx}: trimmed ${(endEl.end_ts - startTime).toFixed(1)}s -> ${fragDur.toFixed(1)}s`);
            }

            logger.debug(`Beat ${beatKey} opt ${optIdx}: idx ${startIdx}-${trimmedEndIdx}, time ${startTime.toFixed(1)}s-${endTime.toFixed(1)}s (${fragDur.toFixed(1)}s), bounds ${beatDurationMin}-${beatDurationMax}s`);

            clipFragment = buildClipFragment({
              partIndex: optIdx,
              startIndex: startIdx,
              endIndex: trimmedEndIdx,
              startTime,
              endTime,
              title,
              textFragment: text,
              transcriptionElements: transcriptElements,
              originalVideo,
              platform: undefined,
              theme: 'reel',
              themeDisplayName: 'Reel',
              durationMin: beatDurationMin,
              durationMax: beatDurationMax,
              speakers,
              stepContext,
              prevResultFragments: [],
              videoTemplateId: videoTemplateId ?? undefined,
            });
          }
        }

        // If index-based failed and we have text, try text-matching fallback
        if (!clipFragment && text && !text.startsWith('Available transcript') && !text.startsWith('No ')) {
          const beginning = stripPunctuationFromEnds(
            text.split(' ').slice(0, NUMBER_OF_WORDS_TO_MATCH).join(' '),
          );
          const ending = stripPunctuationFromEnds(
            text.split(' ').slice(-NUMBER_OF_WORDS_TO_MATCH).join(' '),
          );

          const beginningPos = getFragmentPosition(beginning, transcriptElements);
          if (beginningPos?.startTime && beginningPos?.transcriptPosition) {
            const endingPos = getFragmentPosition(ending, transcriptElements, beginningPos.transcriptPosition.startIndex);
            if (endingPos?.endTime && endingPos?.transcriptPosition) {
              logger.debug(`Beat ${beatKey} opt ${optIdx}: text-match fallback ${beginningPos.startTime.toFixed(1)}s-${endingPos.endTime.toFixed(1)}s`);

              clipFragment = buildClipFragment({
                partIndex: optIdx,
                startIndex: beginningPos.transcriptPosition.startIndex,
                endIndex: endingPos.transcriptPosition.endIndex,
                startTime: beginningPos.startTime,
                endTime: endingPos.endTime,
                title,
                textFragment: text,
                transcriptionElements: transcriptElements,
                originalVideo,
                platform: undefined,
                theme: 'reel',
                themeDisplayName: 'Reel',
                durationMin: beatDurationMin,
                durationMax: beatDurationMax,
                speakers,
                stepContext,
                prevResultFragments: [],
                videoTemplateId: videoTemplateId ?? undefined,
              });
            }
          }
        }

        // Compute reject reason if clip wasn't resolved
        let rejectReason: string | undefined;
        if (!clipFragment) {
          if (startIdx < 0 && endIdx < 0 && (!text || text.length < 10)) {
            rejectReason = 'No text or indices from AI';
          } else if (startIdx >= 0 && endIdx >= 0) {
            // Had indices but buildClipFragment rejected it
            rejectReason = `Indices ${startIdx}-${endIdx} rejected by clip builder (bounds ${beatDurationMin}-${beatDurationMax}s)`;
          } else {
            rejectReason = 'Text not found in transcript';
          }
        }

        logger.debug(`Beat ${beatKey} opt ${optIdx}: ${clipFragment ? `RESOLVED (${clipFragment.startTime.toFixed(1)}s, ${clipFragment.duration.toFixed(1)}s)` : rejectReason}`);

        options.push({
          title,
          startIndex: clipFragment?.transcriptPosition?.startIndex ?? startIdx,
          endIndex: clipFragment?.transcriptPosition?.endIndex ?? endIdx,
          text,
          scores: {
            relevance: scores.relevance ?? 0,
            narrativeFlow: scores.narrative_flow ?? 0,
            speakerMatch: scores.speaker_match ?? 0,
            completeness: scores.completeness ?? 0,
          },
          overallScore,
          recommended,
          justification,
          clipFragment: clipFragment ?? undefined,
          rejectReason,
        });

        if (recommended) selectedOptionIndex = optIdx;
      }

      // Fall back to highest score if no recommended
      if (!options.some(o => o.recommended) && options.length > 0) {
        let bestIdx = 0;
        for (let i = 1; i < options.length; i++) {
          if (options[i].overallScore > options[bestIdx].overallScore) bestIdx = i;
        }
        selectedOptionIndex = bestIdx;
      }

      results.set(beatKey, {
        chapterTitle: meta.chapterTitle,
        beatDescription: meta.beatDescription,
        options,
        selectedOptionIndex,
      });
    }

    const resolvedCount = Array.from(results.values()).reduce(
      (n, r) => n + (r.options[r.selectedOptionIndex]?.clipFragment ? 1 : 0), 0,
    );
    logger.info(`Index-based mapping complete: ${results.size} beats, ${resolvedCount} resolved`);
    return results;
  };
}

/**
 * Orchestrates reel clip generation by loading the AI Flow, building context,
 * and running the flow with a text-based response mapper.
 */
export async function produceReelClips(
  params: ProduceReelClipsParams,
): Promise<ReelClipResults> {
  const {
    outline,
    transcriptElements,
    originalVideo,
    videoDuration,
    rootStore,
    videoCreator,
    allSpeakers,
    selectedSpeakers,
    selectedGoal,
    selectedAudiences,
    videoTemplateId,
    onPartialResults,
  } = params;

  logger.info('Starting reel clip generation', {
    beats: outline.chapters.reduce((n, ch) => n + ch.beats.length, 0),
    totalTargetDuration: outline.totalTargetDuration,
  });

  // 1. Load the AI Flow
  const aiFlow = await rootStore.datoClientStore.aiFlowRepository.load({
    title: 'Reel Clip Producer',
    category: 'reel_producer',
  });

  if (!aiFlow) {
    logger.error('No "Reel Clip Producer" AI flow found in DatoCMS');
    return new Map();
  }

  // 2. Build speaker label map from ALL speakers (not just selected) so names are always mapped
  const speakerLabelToName = buildSpeakerLabelMap(allSpeakers.length > 0 ? allSpeakers : selectedSpeakers);

  // 3. Build context variables
  let goalContext = '';
  if (selectedGoal && selectedGoal.id !== 'none') {
    const goalPrompt =
      await rootStore.datoClientStore.aiPromptRepository.getPromptByTitle(
        'Clip Producer | Context | Goal',
      );
    if (goalPrompt) {
      goalContext = `${goalPrompt.description}\n\n${selectedGoal.label}: ${selectedGoal.finalPrompt}`;
    }
  }

  let audiencesContext = '';
  if (selectedAudiences && selectedAudiences.length > 0) {
    const audiencePrompt =
      await rootStore.datoClientStore.aiPromptRepository.getPromptByTitle(
        'Clip Producer | Context | Audiences',
      );
    if (audiencePrompt) {
      audiencesContext = `${audiencePrompt.description}\n\n${selectedAudiences
        .map(a => `${a.label}: ${a.finalPrompt}`)
        .join('\n\n')}`;
    }
  }

  const speakerContext = buildSpeakerContext(selectedSpeakers);

  // Build transcript in multiple formats to match what the prompt template may use
  const storyTranscription = buildPlainTranscriptWithSpeakers(
    transcriptElements,
    speakerLabelToName,
  );
  const transcriptWithIndices = buildTranscriptWithIndices(
    transcriptElements,
    speakerLabelToName,
  );
  const textElements = transcriptElements.filter(
    el => el.type === 'text' && el.state !== 'removed' && el.ts != null,
  );
  const storyLength = textElements.length;

  // Total target beats
  const totalBeats = outline.chapters.reduce((n, ch) => n + ch.beats.length, 0);
  // Average beat duration for clip duration hints
  const avgBeatDuration = totalBeats > 0
    ? Math.round(outline.totalTargetDuration / totalBeats)
    : 15;

  aiFlow.context = {
    // Reel-specific context
    reelOutline: JSON.stringify(outline),
    totalTargetDuration: outline.totalTargetDuration,
    totalBeats,

    // Standard clip producer context variables (for prompt template compatibility)
    storyTranscription,
    storyTranscriptionPlainText: storyTranscription,
    transcriptWithIndices,
    transcriptCompact: textElements
      .map(el => `${el.value}|${el.ts}`)
      .join(' '),
    transcriptWithTimestamps: textElements
      .map(el => `(word: ${el.value}, time: ${el.ts})`)
      .join(', '),
    storyLength,
    storyDuration: videoDuration,
    chunksTotal: 1,

    // Duration constraints — each beat's duration is in the outline
    // These are hints for the prompt; actual enforcement is per-beat in the mapper
    clipDurationMin: Math.max(5, Math.floor(avgBeatDuration * 0.7)),
    clipDurationMax: Math.ceil(avgBeatDuration * 1.3),
    clipDuration: avgBeatDuration,
    clipLength: Math.round(avgBeatDuration * 2.5), // ~2.5 words/sec speech rate
    clipLengthMin: Math.round(Math.max(5, avgBeatDuration * 0.7) * 2.5),
    clipLengthMax: Math.round(avgBeatDuration * 1.3 * 2.5),

    // Theme / targeting context
    clipTheme: '',
    socialPlatform: '',
    goalContext,
    audiencesContext,
    speakerContext,
  };

  // 4. Set logging context
  const identity = rootStore.userIdentityStore.currentIdentity;
  aiFlow.logContext = {
    aiFlowId: aiFlow.id,
    personId: rootStore.datoPersonStore.person?.id,
    storyId: videoCreator.story?.id,
    showcaseId: videoCreator.organization?.id,
    metadata: {
      user_email: identity?.email,
      user_id: identity?.id,
      dato_environment: rootStore.datoClientStore.environment,
      outline_title: outline.title,
      total_beats: outline.chapters.reduce(
        (n, ch) => n + ch.beats.length,
        0,
      ),
    },
  };

  const userEmail = identity?.email ?? 'unknown';
  const showcaseTitle = videoCreator.organization?.title ?? 'Unknown Showcase';
  const storyTitle = videoCreator.story?.title ?? 'Unknown Story';
  aiFlow.title = `${userEmail} | Reel Producer | Showcase: ${showcaseTitle} | Story: ${storyTitle}`;

  // 5. Enable object streaming and set custom mapper.
  // Prompts are managed in DatoCMS (see migration n01_PLAT9413).
  aiFlow.steps[0].stream = 'object';
  aiFlow.steps[0].mapper = createReelClipResultMapper({
    outline,
    transcriptElements,
    originalVideo,
    speakers: selectedSpeakers,
    videoTemplateId,
  });

  // 6. Enrich with impact score — must run after prompts are finalized so
  // addImpactScoreToStepsConfigIfUsed can detect {{AIPrompt.impactScore}} in
  // the user prompt template loaded from DatoCMS.
  await enrichAIFlowWithImpactScore(aiFlow, rootStore);

  // 7. Wire up partial result callback so clips appear incrementally
  if (onPartialResults) {
    let lastEmittedSize = 0;
    aiFlow.steps[0].onPartialResult = (result: ReelClipResults | null) => {
      if (result && result.size > 0 && result.size !== lastEmittedSize) {
        lastEmittedSize = result.size;
        onPartialResults(result);
      }
    };
  }

  // 8. Run the flow
  await aiFlow.run();

  // 9. Extract result
  const result = aiFlow.result as ReelClipResults | null;
  if (!result) {
    logger.warn('Reel clip generation flow returned no result');
    return new Map();
  }

  return result;
}

